import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  listUserPolicies,
  toggleUserPolicy,
  getUserPolicySettings,
  updateUserPolicySettings,
  deleteUserPolicySettings,
} from '@kirin/api-client/api/policies';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import type {
  PolicyList,
  PolicySettings,
  PolicySettingsUpdate,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';

/**
 * Hook to fetch user policies list
 *
 * Uses React Query for caching and automatic refetching.
 * Policies are cached for 5 minutes.
 *
 * @example
 * const { data, isLoading, error } = useUserPolicies();
 *
 * @params select - Optional selector function to transform data
 *
 * @returns React Query result with policies array
 */
export function useUserPolicies(
  options?: Omit<
    UseQueryOptions<PolicyList['policies']>,
    'queryKey' | 'queryFn' | 'retry'
  >
) {
  return useQuery({
    queryKey: queryKeys.policies.user,
    queryFn: async () => {
      const result = await listUserPolicies(apiClient);
      return result.policies;
    },
    retry: retryHandlers.list,
    ...options,
  });
}

/**
 * Hook to fetch policy settings for a user policy
 *
 * @param policyKey - The policy key to fetch settings for
 * @param enabled - Whether to enable the query (default: true if policyKey provided)
 *
 * @example
 * const { data: settings, isLoading } = useUserPolicySettings('mcp-server-sources');
 *
 * @returns React Query result with policy settings
 */
export function useUserPolicySettings(
  policyKey: string | undefined,
  enabled = true,
  options?: Omit<
    UseQueryOptions<PolicySettings>,
    'queryKey' | 'queryFn' | 'enabled' | 'retry'
  >
): ReturnType<typeof useQuery<PolicySettings>> {
  const isEnabled = enabled && !!policyKey;
  return useQuery<PolicySettings>({
    queryKey: queryKeys.policies.settings(policyKey ?? ''),
    queryFn: async () => {
      if (!policyKey) throw new Error('policyKey is required');
      const result = await getUserPolicySettings(apiClient, policyKey);
      return result;
    },
    enabled: isEnabled && !!policyKey,
    retry: retryHandlers.detail,
    ...options,
  });
}

/**
 * Hook to fetch policy settings for multiple user policies
 *
 * Uses React Query useQueries for fetching multiple settings in parallel.
 * Returns a map of policyKey -> settings data and loading states.
 *
 * @param policyKeys - Array of policy keys to fetch settings for
 * @param enabled - Whether to enable the queries (default: true)
 *
 * @example
 * const { policySettings, loadingSettings, isLoading } = useUserPolicySettingsList(['policy1', 'policy2']);
 *
 * @returns Object with policySettings map, loadingSettings set, and isLoading flag
 */
export function useUserPolicySettingsList(
  policyKeys: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: policyKeys.map(policyKey => ({
      queryKey: queryKeys.policies.settings(policyKey),
      queryFn: async () => {
        const result = await getUserPolicySettings(apiClient, policyKey);
        return result;
      },
      enabled: enabled && !!policyKey,
      retry: retryHandlers.detail,
    })),
  });

  const policySettings = useMemo(() => {
    const settings: Record<string, PolicySettings> = {};
    policyKeys.forEach((policyKey, index) => {
      const query = queries[index];
      if (query?.data) {
        settings[policyKey] = query.data;
      }
    });
    return settings;
  }, [policyKeys, queries]);

  const loadingSettings = useMemo(() => {
    const loading = new Set<string>();
    policyKeys.forEach((policyKey, index) => {
      const query = queries[index];
      if (query?.isLoading || query?.isFetching) {
        loading.add(policyKey);
      }
    });
    return loading;
  }, [policyKeys, queries]);

  const isLoading = queries.some(query => query.isLoading || query.isFetching);

  return {
    policySettings,
    loadingSettings,
    isLoading,
  };
}

/**
 * Hook to toggle a user policy
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync, isPending } = useToggleUserPolicy();
 * await mutateAsync({ policyKey: 'mcp-server-sources', enabled: true });
 *
 * @returns React Query mutation result
 */
export function useToggleUserPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyKey,
      enabled,
      mode,
    }: {
      policyKey: string;
      enabled: boolean;
      mode?: PolicyModeType;
    }) => {
      const result = await toggleUserPolicy(
        apiClient,
        policyKey,
        enabled,
        mode
      );
      return result;
    },
    onSuccess: () => {
      // Invalidate policies list to refresh the enabled state
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
    },
  });
}

/**
 * Hook to update user policy settings
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync, isPending } = useUpdateUserPolicySettings();
 * await mutateAsync({ policyKey: 'mcp-server-sources', settings: {...} });
 *
 * @returns React Query mutation result
 */
export function useUpdateUserPolicySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settingsUpdate: PolicySettingsUpdate) => {
      const result = await updateUserPolicySettings(apiClient, settingsUpdate);
      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific policy settings cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.settings(variables.policyKey),
      });
      // Also invalidate policies list to update customizedSettings count
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
    },
  });
}

/**
 * Hook to reset user policy settings (delete all or specific setting)
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync, isPending } = useResetUserPolicySettings();
 * await mutateAsync({ policyKey: 'mcp-server-sources', settingKey: 'trustedSources' });
 *
 * @returns React Query mutation result
 */
export function useResetUserPolicySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyKey,
      settingKey,
    }: {
      policyKey: string;
      settingKey?: string;
    }) => {
      const result = await deleteUserPolicySettings(
        apiClient,
        policyKey,
        settingKey
      );
      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific policy settings cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.settings(variables.policyKey),
      });
      // Also invalidate policies list to update customizedSettings count
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.user });
    },
  });
}
